import { Contract } from '../types';
import { supabase } from './supabase';

// Fallback to localStorage if Supabase is not configured
const STORAGE_KEY = 'alex_cody_contracts';

const isSupabaseConfigured = () => {
  return import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY;
};

// LocalStorage helpers
const getLocalContracts = (): Contract[] => {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : [];
};

const saveLocalContract = (contract: Contract): void => {
  const contracts = getLocalContracts();
  const existingIndex = contracts.findIndex(c => c.id === contract.id);
  
  if (existingIndex >= 0) {
    contracts[existingIndex] = contract;
  } else {
    contracts.push(contract);
  }
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(contracts));
};

const deleteLocalContract = (id: string): void => {
  const contracts = getLocalContracts().filter(c => c.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(contracts));
};

// Helper to convert snake_case to camelCase
const mapDbToContract = (db: any): Contract => ({
  id: db.id,
  title: db.title,
  description: db.description || '',
  creator: db.creator,
  createdAt: db.created_at,
  deadline: db.deadline,
  status: db.status,
  reward: db.reward,
  penalty: db.penalty,
  completedAt: db.completed_at,
  notes: db.notes
});

const mapContractToDb = (c: Contract): any => ({
  id: c.id,
  title: c.title,
  description: c.description,
  creator: c.creator,
  created_at: c.createdAt,
  deadline: c.deadline,
  status: c.status,
  reward: c.reward,
  penalty: c.penalty,
  completed_at: c.completedAt,
  notes: c.notes
});

// Supabase helpers
const getSupabaseContracts = async (): Promise<Contract[]> => {
  const { data, error } = await supabase
    .from('contracts')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Supabase error:', error);
    return getLocalContracts();
  }
  
  return (data || []).map(mapDbToContract);
};

const saveSupabaseContract = async (contract: Contract): Promise<void> => {
  const { error } = await supabase
    .from('contracts')
    .upsert(mapContractToDb(contract));
  
  if (error) {
    console.error('Supabase error:', error);
    saveLocalContract(contract);
  }
};

const deleteSupabaseContract = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('contracts')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Supabase error:', error);
    deleteLocalContract(id);
  }
};

// Unified API
export const getContracts = async (): Promise<Contract[]> => {
  if (isSupabaseConfigured()) {
    return getSupabaseContracts();
  }
  return getLocalContracts();
};

export const saveContract = async (contract: Contract): Promise<void> => {
  if (isSupabaseConfigured()) {
    return saveSupabaseContract(contract);
  }
  saveLocalContract(contract);
};

export const deleteContract = async (id: string): Promise<void> => {
  if (isSupabaseConfigured()) {
    return deleteSupabaseContract(id);
  }
  deleteLocalContract(id);
};

export const getActiveContracts = async (): Promise<Contract[]> => {
  const contracts = await getContracts();
  return contracts.filter(c => c.status === 'active');
};

export const getCompletedContracts = async (): Promise<Contract[]> => {
  const contracts = await getContracts();
  return contracts.filter(c => c.status === 'completed');
};

export const getOverdueContracts = async (): Promise<Contract[]> => {
  const now = new Date().toISOString();
  const contracts = await getContracts();
  return contracts.filter(c => 
    c.status === 'active' && c.deadline < now
  );
};
